/**
 * WorkLog Component
 * Component for managing task work logs
 * Handles time tracking and work log entries
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, Button, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { REGEX } from '../../shared/regex';
import { ICONS } from '../../shared/icons';
import './TaskModal.scss';

const WorkLog = ({ ticketId, worklogSummary, worklogSummaryLoading, onCreateWorklog, onUpdateWorklog, onDeleteWorklog, user }) => {
  const { t } = useTranslation();
  const [timeSpent, setTimeSpent] = useState('');
  const [timeError, setTimeError] = useState('');
  const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editEntryId, setEditEntryId] = useState(null);
  const [editTimeSpent, setEditTimeSpent] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTimeError, setEditTimeError] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuEntryId, setMenuEntryId] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const hoursMatch = timeStr.match(/(\d+)h/i);
    const minutesMatch = timeStr.match(/(\d+)m/i);
    const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
    return hours * 60 + minutes;
  };

  const validateTimeFormat = (value) => {
    if (!value.trim()) {
      setTimeError('');
      return true;
    }
    if (!REGEX.timeFormat.test(value.trim())) {
      setTimeError(t('workLog.formatError'));
      return false;
    }
    
    // Check if minutes are valid (0-59)
    const minutesMatch = value.match(REGEX.minutes);
    if (minutesMatch) {
      const minutes = parseInt(minutesMatch[1]);
      if (minutes > 59) {
        setTimeError(t('workLog.minutesError'));
        return false;
      }
    }

    // Check 24-hour daily limit
    const newTimeMinutes = parseTimeToMinutes(value.trim());
    const currentUserId = user?.id || user?.user_id;
    const existingDailyMinutes = worklogSummary?.work_logs
      ?.filter(log => log.log_date === logDate && log.user_id === currentUserId)
      ?.reduce((total, log) => total + parseTimeToMinutes(log.time_spent), 0) || 0;
    
    if (newTimeMinutes + existingDailyMinutes > 1440) { // 24 hours = 1440 minutes
      setTimeError('Cannot exceed 24 hours per day');
      return false;
    }
    
    setTimeError('');
    return true;
  };

  const validateEditTimeFormat = (value) => {
    if (!value.trim()) {
      setEditTimeError('');
      return true;
    }
    if (!REGEX.timeFormat.test(value.trim())) {
      setEditTimeError(t('workLog.formatError'));
      return false;
    }
    const minutesMatch = value.match(REGEX.minutes);
    if (minutesMatch) {
      const minutes = parseInt(minutesMatch[1]);
      if (minutes > 59) {
        setEditTimeError(t('workLog.minutesError'));
        return false;
      }
    }

    // Check 24-hour daily limit for edit
    const newTimeMinutes = parseTimeToMinutes(value.trim());
    const currentUserId = user?.id || user?.user_id;
    const existingDailyMinutes = worklogSummary?.work_logs
      ?.filter(log => log.log_date === editDate && log.log_id !== editEntryId && log.user_id === currentUserId)
      ?.reduce((total, log) => total + parseTimeToMinutes(log.time_spent), 0) || 0;
    
    if (newTimeMinutes + existingDailyMinutes > 1440) { // 24 hours = 1440 minutes
      setEditTimeError('Cannot exceed 24 hours per day');
      return false;
    }

    setEditTimeError('');
    return true;
  };

  const handleTimeChange = (e) => {
    const value = e.target.value;
    setTimeSpent(value);
    validateTimeFormat(value);
  };

  const handleStartEdit = (entry) => {
    setEditEntryId(entry.log_id);
    setEditTimeSpent(entry.time_spent);
    setEditDescription(entry.message);
    setEditTimeError('');
    setEditDate(entry.log_date || logDate);
  };

  const handleCancelEdit = () => {
    setEditEntryId(null);
    setEditTimeSpent('');
    setEditDescription('');
    setEditTimeError('');
  };

  const handleSaveEdit = async () => {
    if (!validateEditTimeFormat(editTimeSpent)) return;
    
    setIsEditSubmitting(true);
    try {
      if (onUpdateWorklog && editEntryId) {
        await onUpdateWorklog(editEntryId, {
          log_date: editDate,
          description: editDescription,
          time_spent: editTimeSpent
        });
      }
      
      handleCancelEdit();
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (onDeleteWorklog) {
      await onDeleteWorklog(id);
    }
    if (editEntryId === id) {
      handleCancelEdit();
    }
  };


  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuEntryId(null);
  };

  const handleLogWork = async () => {
    if (!validateTimeFormat(timeSpent)) return;
    const trimmedDescription = description.trim();
    if (!trimmedDescription) return;
    
    setIsSubmitting(true);
    try {
      if (onCreateWorklog && ticketId) {
        // Call API to create worklog
        await onCreateWorklog({
          log_date: logDate,
          description: trimmedDescription,
          time_spent: timeSpent.trim()
        });
      }

      setTimeSpent('');
      setDescription('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 3, color: COLORS.gray[800] }}>
        {t('workLog.workLog')}
      </Typography>
      
      {/* Time Summary Card */}
      <Box sx={{
        border: `2px solid #082B72`,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'stretch',
        mb: 3,
        overflow: 'hidden'
      }}>
        {/* Left stats */}
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <Box textAlign="center" sx={{ flex: 1, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="500" sx={{ color: '#082B72' }}>
              {worklogSummaryLoading ? '-' : worklogSummary?.work_logs?.length || 0}
            </Typography>
            <Typography variant="caption" className="worklog-label" sx={{ color: '#656565', fontSize: '0.72rem' }}>
              {t('workLog.entries')}
            </Typography>
          </Box>
          <Box textAlign="center" sx={{ flex: 1, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="500" sx={{ color: '#082B72' }}>
              {worklogSummaryLoading ? '-' : worklogSummary?.weekly_time_spent || '0h 0m'}
            </Typography>
            <Typography variant="caption" className="worklog-label" sx={{ color: '#656565', fontSize: '0.72rem' }}>
              {t('workLog.thisWeek')}
            </Typography>
          </Box>
          <Box textAlign="center" sx={{ flex: 1, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="500" sx={{ color: '#082B72' }}>
              {worklogSummaryLoading ? '-' : worklogSummary?.average_time_per_day || '0h 0m'}
            </Typography>
            <Typography variant="caption" className="worklog-label" sx={{ color: '#656565', fontSize: '0.72rem' }}>
              {t('workLog.avgDay')}
            </Typography>
          </Box>
        </Box>

        {/* Vertical Divider */}
        <Box sx={{ width: '1px', bgcolor: COLORS.gray[300], my: 1.5 }} />

        {/* Right — Total Time */}
        <Box textAlign="right" sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 150 }}>
          <Typography variant="h6" fontWeight="600" sx={{ color: '#082B72', fontSize: '1.35rem' }}>
            {worklogSummaryLoading ? t('workLog.loading') : worklogSummary?.total_time_spent || '0h 0m'}
          </Typography>
          <Typography variant="caption" className="worklog-total-label" sx={{ color: '#656565', fontSize: '0.82rem' }}>
            {t('workLog.totalTimeLogged')}
          </Typography>
        </Box>
      </Box>
      
      {/* Log Work Form */}
      <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, color: COLORS.gray[800] }}>
        {t('workLog.logWork')}
      </Typography>
      
      <Box display="flex" gap={2} mb={2}>
        <TextField
          size="small"
          placeholder={t('workLog.timeSpentPlaceholder')}
          value={timeSpent}
          onChange={handleTimeChange}
          error={!!timeError}
          helperText={timeError}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          type="date"
          value={logDate}
          onChange={(e) => setLogDate(e.target.value)}
          onKeyDown={(e) => e.preventDefault()}
          sx={{ width: 150 }}
        />
      </Box>
      
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder={t('workLog.whatDidYouWorkOn')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ mb: 2 }}
      />
      
      <Button
        variant="contained"
        fullWidth
        sx={{
          bgcolor: COLORS.primary,
          py: 1.5,
          fontSize: 16,
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': { bgcolor: COLORS.blue[700] },
          mb: 3
        }}
        onClick={handleLogWork}
        disabled={isSubmitting || !!timeError || !timeSpent.trim() || !description.trim()}
      >
        {isSubmitting ? t('workLog.saving') : t('workLog.logWork')}
      </Button>
      
      {/* Work Log Entries */}
      <Box sx={{ mb: 3 }}>
        {worklogSummaryLoading ? (
          <Typography>{t('workLog.loadingWorkLogs')}</Typography>
        ) : worklogSummary?.work_logs?.length > 0 ? (
          worklogSummary.work_logs.map((entry) => (
          <Box key={entry.log_id} sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${COLORS.gray[200]}` }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography variant="body2" fontWeight="600" sx={{ color: '#082B72' }}>
                {entry.user_name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">
                  {entry.log_date}
                </Typography>
                <Box sx={{ width: 'auto', display: 'flex', justifyContent: 'center' }}>
                  {user && (user?.id === entry.user_id || user?.user_id === entry.user_id) && (
                    <Box sx={{ display: 'flex', bgcolor: '#F0F0F0', borderRadius: '8px', p: 0.5, gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit(entry)}
                        sx={{ '&:hover': { bgcolor: '#E0E0E0' }, p: 0.75, borderRadius: '6px' }}
                      >
                        <img src={ICONS.edit} alt="edit" style={{ width: 14, height: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(entry.log_id)}
                        sx={{ '&:hover': { bgcolor: '#E0E0E0' }, p: 0.75, borderRadius: '6px' }}
                      >
                        <img src={ICONS.delete} alt="delete" style={{ width: 14, height: 14 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {editEntryId === entry.log_id ? (
              <Box>
                <Box display="flex" gap={2} mb={1}>
                  <TextField
                    size="small"
                    placeholder={t('workLog.timeSpentPlaceholder')}
                    value={editTimeSpent}
                    onChange={(e) => {
                      setEditTimeSpent(e.target.value);
                      validateEditTimeFormat(e.target.value);
                    }}
                    error={!!editTimeError}
                    helperText={editTimeError}
                    sx={{ width: 220 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    sx={{ width: 170 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t('workLog.whatDidYouWorkOn')}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <Button variant="contained" size="small" onClick={handleSaveEdit} disabled={isEditSubmitting || !!editTimeError || !editTimeSpent.trim() || !editDescription.trim()}>
                    {isEditSubmitting ? t('workLog.saving') : t('workLog.save')}
                  </Button>
                  <Button variant="text" size="small" onClick={handleCancelEdit} disabled={isEditSubmitting}>{t('workLog.cancel')}</Button>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="h6" fontWeight="600" sx={{ color: COLORS.gray[800], mb: 1 }}>
                  {entry.time_spent}
                </Typography>
                <Box className="description-container">
                  <Typography className="comment-content" variant="body2" color="text.secondary">
                    {entry.message}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
            {t('workLog.noWorkLogsFound')}
          </Typography>
        )}
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { const entry = worklogSummary?.work_logs?.find((e) => e.log_id === menuEntryId); if (entry) handleStartEdit(entry); closeMenu(); }} sx={{ justifyContent: 'center' }}>
          <Tooltip title={t('workLog.editEntry')} placement="left" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
            <IconButton size="small" aria-label={t('workLog.editEntry')}>
              <Edit sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </MenuItem>
        <MenuItem onClick={() => { if (menuEntryId) handleDeleteEntry(menuEntryId); closeMenu(); }} sx={{ justifyContent: 'center' }}>
          <Tooltip title={t('workLog.deleteEntry')} placement="left" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
            <IconButton size="small" aria-label={t('workLog.deleteEntry')}>
              <Delete sx={{ fontSize: 18, color: COLORS.error }} />
            </IconButton>
          </Tooltip>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WorkLog;